import { useRef, useState } from 'react';
import { Images } from 'lucide-react';
import { AsciiDocContent } from '@/components/AsciiDocContent';
import { AsciiDocToolbar, handleAsciiDocEditorKeyDown } from '@/components/AsciiDocToolbar';
import { LiveEditor } from '@/components/LiveEditor';
import { ScrollableToolbarRow } from '@/components/ScrollableToolbarRow';
import { WikilinkSuggestionPopup } from '@/components/WikilinkSuggestionPopup';
import { EditorViewToggle, type EditorViewMode } from '@/components/EditorViewToggle';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useDjotEditor } from '@/hooks/useDjotEditor';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWikilinkAutocomplete } from '@/hooks/useWikilinkAutocomplete';
import { cn } from '@/lib/utils';

interface AsciiDocEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Minimum height of the editing surface. Defaults to 420px. */
  minHeight?: number;
  /** When true, the editor fills its flex parent instead of capping at `minHeight` — used by focus mode for full-page editing. */
  fullHeight?: boolean;
  /** Raw/live/split layout control — optional; the editor uses its own state if not provided. */
  viewMode?: EditorViewMode;
  onViewModeChange?: (mode: EditorViewMode) => void;
  /** Replaces the toolbar's plain link action with a media-library picker. */
  onRequestMedia?: () => void;
  /** Called on Ctrl/Cmd+Shift+F while editing. */
  onToggleFocus?: () => void;
  /** Called on Ctrl/Cmd+S while editing. */
  onSave?: () => void;
}

/**
 * A full AsciiDoc markup editing experience for NKBIP-01 publication
 * sections: a formatting toolbar (headings, bold/italic/mono, lists,
 * admonitions, links, tables, …), a plain raw-text editor, and a
 * `LiveEditor` surface that's directly editable and styled to look like
 * the rendered result as you type — both driven by the same
 * `applyEdit`/toolbar plumbing, so formatting actions work identically
 * no matter which surface has focus.
 *
 * The body offers three view modes via `EditorViewToggle`:
 *
 *  - **Raw edit** — just the plain-text editor, full width.
 *  - **Live edit** — just `LiveEditor`, full width: directly editable,
 *    styled to look like the rendered result as you type.
 *  - **Split editor** — the raw-text editor next to a true **read-only**
 *    rendered preview (`AsciiDocContent`, the same renderer published
 *    sections use), NOT `LiveEditor` — see `DjotEditor`'s doc comment
 *    for the rationale.
 *
 * Narrow screens default to raw edit; wide screens default to split —
 * but the mode is always switchable regardless of viewport.
 *
 * `[[wikilink]]` autocomplete is shared with the Djot editor
 * (`useWikilinkAutocomplete`) — AsciiDoc's double-bracket wikilink syntax
 * is native (per NKBIP-01: "content MAY contain wikilinks, denoted by
 * double brackets"), so no expansion step is needed at publish time,
 * unlike the Djot editor's `[[...]]` shorthand.
 */
export function AsciiDocEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = 420,
  fullHeight = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  onRequestMedia,
  onToggleFocus,
  onSave,
}: AsciiDocEditorProps) {
  // `useDjotEditor` is format-agnostic (plain textarea selection
  // bookkeeping) despite the name — shared across both editors.
  const { bindActive, applyEdit } = useDjotEditor({ value, onChange });
  const isMobile = useIsMobile();
  const [internalViewMode, setInternalViewMode] = useState<EditorViewMode>(() => (isMobile ? 'write' : 'split'));
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  const rawTextareaRef = useRef<HTMLTextAreaElement>(null);
  const wikilink = useWikilinkAutocomplete({ textareaRef: rawTextareaRef, value, onChange });

  const rawEditorEl = (
    <div className="relative h-full">
      <Textarea
        ref={(el) => {
          rawTextareaRef.current = el;
          bindActive(el);
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => bindActive(e.currentTarget)}
        onKeyDown={(e) => {
          if (wikilink.isOpen && wikilink.handleKeyDown(e)) return;
          handleAsciiDocEditorKeyDown(e, applyEdit, {
            onToggleFocus,
            onSave,
          });
        }}
        onSelect={wikilink.onSelectionChange}
        onClick={wikilink.onSelectionChange}
        onKeyUp={wikilink.onSelectionChange}
        onScroll={wikilink.onSelectionChange}
        onBlur={wikilink.close}
        placeholder={placeholder}
        spellCheck
        className="h-full min-h-full resize-none rounded-none border-0 font-mono text-sm shadow-none focus-visible:ring-0"
      />
      {wikilink.isOpen && wikilink.position && (
        <WikilinkSuggestionPopup
          position={wikilink.position}
          suggestions={wikilink.suggestions}
          activeIndex={wikilink.activeIndex}
          isLoading={wikilink.isLoading}
          query={wikilink.query}
          onHover={wikilink.setActiveIndex}
          onSelect={wikilink.select}
        />
      )}
    </div>
  );

  const liveEditorEl = (
    <LiveEditor
      value={value}
      onChange={onChange}
      format="asciidoc"
      placeholder={placeholder}
      registerTextarea={bindActive}
      onFormatKeyDown={(e) =>
        handleAsciiDocEditorKeyDown(e, applyEdit, {
          onToggleFocus,
          onSave,
        })
      }
    />
  );

  // Split mode's right pane is a true read-only preview — the same
  // renderer published sections use — not the editable LiveEditor. See
  // the component doc comment above.
  const readOnlyPreviewEl = (
    <div className="h-full overflow-y-auto p-4">
      {value.trim() ? (
        <AsciiDocContent content={value} />
      ) : (
        <p className="text-sm text-muted-foreground">Nothing to preview yet — start writing.</p>
      )}
    </div>
  );

  return (
    <div className={cn('space-y-0', fullHeight && 'flex min-h-0 h-full flex-col', className)}>
      {/* Single scrollable row — see DjotEditor. Insert media leads the
          row for the same reason as there: it's one of the most-used
          actions, so it sits at the far left rather than buried after the
          formatting controls. */}
      <ScrollableToolbarRow className="rounded-t-md border border-b-0 border-input bg-muted/40 p-1">
        {onRequestMedia && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={onRequestMedia}
              aria-label="Insert media"
            >
              <Images className="size-4" />
            </Button>
            <Separator orientation="vertical" className="mx-1 h-5" />
          </>
        )}
        <AsciiDocToolbar applyEdit={applyEdit} />
      </ScrollableToolbarRow>

      {viewMode === 'write' && (
        <div
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          {rawEditorEl}
        </div>
      )}

      {viewMode === 'live' && (
        <div
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          {liveEditorEl}
        </div>
      )}

      {viewMode === 'split' && (
        <ResizablePanelGroup
          direction="horizontal"
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          <ResizablePanel defaultSize={50} minSize={25}>
            {rawEditorEl}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={50} minSize={25}>
            {readOnlyPreviewEl}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
}
